<template>
  <div>
    <v-form ref="accountForm" class="multi-col-validation custom-form">
      <v-row>
        <v-col cols="6" class="input-padding">
          <label class="text--primary text-xs2 required-mark">{{ 'Subject' }}</label>
          <v-autocomplete
            v-model="modalFormData.subject"
            :items="userOptions"
            data-cy="log-subject"
            outlined
            :rules="[required]"
            dense
            autocomplete="null"
            class="custom-input"
            placeholder="Subject"
          >
          </v-autocomplete>
        </v-col>

        <v-col cols="6" class="input-padding">
          <label class="text--primary text-xs2 required-mark">{{ 'Contact' }}</label>
          <autocomplete-view
            v-if="isVisibleContactName"
            :label="defaultContactName"
            @handleClose="isVisibleContactName = false"
          />

          <v-autocomplete
            v-else
            v-model="modalFormData.contact_id"
            :items="contactArray"
            :search-input.sync="contactSearch"
            :rules="[required]"
            data-cy="log-contact"
            outlined
            dense
            autocomplete="null"
            item-text="name"
            item-value="uuid"
            class="custom-input"
            placeholder="Contact"
            @update:search-input="processContactInput"
          >
            <template v-slot:item="data">
              <v-list-item-title class="text-xs3">
                <div>{{ data.item.name }}</div>
                <div v-if="data.item.related_to" class="text-xs">
                  {{ data.item.related_to.name }}
                </div>
              </v-list-item-title>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="6" class="input-padding">
          <label class="text--primary text-xs2 required-mark">{{ 'Related To' }}</label>
          <autocomplete-view
            v-if="isVisibleName"
            :label="defaultPropName"
            @handleClose="isVisibleName = false"
          />
          <v-autocomplete
            v-else
            v-model="modalFormData.prop_id"
            :items="relatedToList"
            :search-input.sync="relatedToSearch"
            outlined
            dense
            :rules="[required]"
            autocomplete="null"
            item-text="name"
            item-value="uuid"
            class="custom-input"
            placeholder="Search..."
            @update:search-input="processInput"
          >
          </v-autocomplete>
        </v-col>

        <v-col cols="6">
          <label class="text--primary text-xs2 required-mark">{{ 'Date' }}</label>
          <v-menu
            v-model="menu2"
            :close-on-content-click="false"
            :nudge-right="5"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="modalFormData.date"
                data-cy="call-log-date"
                :prepend-inner-icon="icons.mdiCalendar"
                readonly
                placeholder="Date"
                :rules="[required]"
                outlined
                dense
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="modalFormData.date"
              data-cy="date-picker-log-date"
              color="primary"
              no-title
              @input="menu2 = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="6" class="input-padding">
          <label class="text--primary text-xs2 required-mark">{{ 'Comment' }}</label>
          <v-textarea
            v-model="modalFormData.comment"
            rows="2"
            data-cy="log-comment"
            placeholder="Comment"
            :rules="[required]"
            outlined
            dense
          >
          </v-textarea>
        </v-col>
      </v-row>
    </v-form>
    <div class="mt-4 text-right">
      <v-btn color="primary" data-cy="log-btn" small :loading="loading" @click.prevent="add">
        Save
      </v-btn>
    </div>
  </div>
</template>

<script>
import { ref, watch } from '@vue/composition-api'
import { required } from '@core/utils/validation'
import { storeActions } from '@/store/storeConstants'
import store from '@/store'
import Toaster from '@core/utils/sweetToast'
import { useRouter } from '@core/utils'
import { ERROR_RESPONSE } from '@/constants/appConstants'
import { mdiCalendar } from '@mdi/js'
import useDebounce from '@/plugins/useDebounce'
import AutocompleteView from '@/components/shared-components/AutocompleteView.vue'

export default {
  components: { AutocompleteView },
  props: {
    propType: {
      type: String,
      required: true,
    },
    propId: {
      type: String,
      default: undefined,
    },
    contactId: {
      type: String,
      default: undefined,
    },
    defaultContactName: {
      type: String,
      default: undefined,
    },
    defaultPropName: {
      type: String,
      default: undefined,
    },
    contactArray: {
      type: Array,
      default: () => [],
    },
    relatedToList: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { CREATE, GET_ALL_RECORDS } = storeActions
    const { route } = useRouter()
    const tab = ref(null)
    const { debounce } = useDebounce()

    const activityTab = ref(null)
    const modalFormData = ref({})
    const contactSearch = ref('')
    const relatedToSearch = ref('')
    const isVisibleName = ref(false)
    const isVisibleContactName = ref(false)

    const accountForm = ref(null)
    const loading = ref(false)
    const userOptions = ref([
      { text: 'Call', value: 0 },
      { text: 'Email', value: 1 },
      { text: 'Send Letter', value: 2 },
      { text: 'Send Quote', value: 3 },
      { text: 'Meeting', value: 5 },
      { text: 'Other', value: 4 },
    ])
    const workflowOptions = ref([])
    const workflowStatusOptions = ref([])
    const menu2 = ref(false)

    const onWorkflowChange = () => {
      const config = {
        component: 'setup/taskManagement/status',
        filter: {
          workflow_uuid: modalFormData.value.workflow_uuid,
        },
      }
      store.dispatch(`common/${GET_ALL_RECORDS}`, config).then(({ data }) => {
        if (data.success) {
          workflowStatusOptions.value = data.data.map(opt => {
            return {
              value: opt.uuid,
              text: opt.name,
            }
          })
        }
      })
    }

    const add = () => {
      const isValid = accountForm.value.validate()
      if (!isValid) return
      loading.value = true
      const payLoad = {
        object: {
          contact_id: props.contactId,
          prop_id: props.propId,
          ...modalFormData.value,
          prop_type: props.propType,
        },
        component: '/task-management/logs',
      }
      store
        .dispatch(`common/${CREATE}`, payLoad)
        .then(({ data }) => {
          if (data.success) {
            modalFormData.value = {}
            accountForm.value.reset()
            emit('fetchData')
            Toaster.success(data.message)
          } else {
            Toaster.error(data.message)
          }
          loading.value = false
        })
        .catch(err => {
          loading.value = false
          Toaster.error(err?.response?.data?.message ?? ERROR_RESPONSE)
        })
    }

    watch(
      () => props.defaultPropName,
      val => {
        if (val) {
          isVisibleName.value = true
        }
      },
      { immediate: true },
    )

    watch(
      () => props.defaultContactName,
      val => {
        if (val) {
          isVisibleContactName.value = true
        }
      },
      { immediate: true },
    )

    const processContactInput = () => {
      // emit event and fetch contact
      debounce(() => emit('fetchContacts', contactSearch.value), 800)
    }

    const processInput = () => {
      // emit event and fetch contact
      debounce(() => emit('fetchRelatedTo', relatedToSearch.value), 500)
    }

    return {
      tab,
      activityTab,
      modalFormData,
      userOptions,
      workflowOptions,
      workflowStatusOptions,
      loading,
      add,
      processInput,
      isVisibleName,
      isVisibleContactName,
      relatedToSearch,
      accountForm,
      required,
      onWorkflowChange,
      menu2,
      contactSearch,
      processContactInput,
      icons: {
        mdiCalendar,
      },
    }
  },
}
</script>

<style scoped lang="scss">
.custom-form .form-sub-header {
  background-color: #f3f3f3;
  color: #000;
  display: inline-block;
  padding: 8px 20px;
  border-radius: 0 0 6px 6px;
}
.input-padding {
  padding-top: 6px;
  padding-bottom: 6px;
}
</style>
